import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, CircularProgress, Fade, Paper, Snackbar, Alert, Typography } from '@mui/material';
import WifiOffRoundedIcon from '@mui/icons-material/WifiOffRounded';
import CloudSyncRoundedIcon from '@mui/icons-material/CloudSyncRounded';
import dayjs from 'dayjs';
import { checkService, useServiceStatus } from '@/global/serviceStatus';

/** seconds between automatic checks: quick at first, then every 30 s */
const DELAYS = [5, 10, 20, 30];

/**
 * Shown over the page while the ERP server cannot be reached (instead of error pages or a 404): says what is wrong,
 * keeps checking by itself and disappears when the connection is back. The page underneath stays as it was, so
 * nothing typed on it is lost.
 */
const ServiceStatusScreen: React.FC = () => {
  const s = useServiceStatus();
  const [left, setLeft] = useState(0);
  const [backAgain, setBackAgain] = useState(false);
  const attempt = useRef(0);
  const wasDown = useRef(false);
  const down = s.state !== 'ok';

  // automatic checks with a countdown
  useEffect(() => {
    if (!down) {
      if (wasDown.current) setBackAgain(true);
      wasDown.current = false;
      attempt.current = 0;
      return;
    }
    wasDown.current = true;
    if (s.checking) return;
    const wait = DELAYS[Math.min(attempt.current, DELAYS.length - 1)];
    setLeft(wait);
    const tick = setInterval(() => setLeft((v) => Math.max(0, v - 1)), 1000);
    const timer = setTimeout(() => {
      attempt.current += 1;
      void checkService();
    }, wait * 1000);
    return () => {
      clearInterval(tick);
      clearTimeout(timer);
    };
  }, [down, s.checking, s.lastCheck]);

  const offline = s.state === 'offline';
  const Icon = offline ? WifiOffRoundedIcon : CloudSyncRoundedIcon;
  const accent = offline ? '#64748b' : '#d97706';

  return (
    <>
      <Fade in={down} unmountOnExit>
        <Box
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="svc-title"
          aria-describedby="svc-text"
          sx={{
            position: 'fixed',
            inset: 0,
            zIndex: 20000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 2,
            bgcolor: 'rgba(15, 23, 42, 0.55)',
            backdropFilter: 'blur(6px)',
          }}
        >
          <Paper elevation={0} sx={{ width: '100%', maxWidth: 460, borderRadius: 4, p: { xs: 3, sm: 4 }, textAlign: 'center', boxShadow: '0 24px 60px rgba(15,23,42,0.35)' }}>
            <Box
              sx={{
                width: 72,
                height: 72,
                mx: 'auto',
                mb: 2,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: `${accent}1a`,
                color: accent,
              }}
            >
              <Icon sx={{ fontSize: 38 }} />
            </Box>
            <Typography id="svc-title" sx={{ fontSize: '1.25rem', fontWeight: 700, color: '#0f172a', mb: 1 }}>
              {offline ? 'You are offline' : 'We will be right back'}
            </Typography>
            <Typography id="svc-text" sx={{ fontSize: '0.9rem', color: '#475569', lineHeight: 1.6, mb: 2.5 }}>
              {offline
                ? 'This computer has lost its network connection. Check the network cable or Wi-Fi; the portal reconnects by itself.'
                : 'The ERP server is not answering right now. It may be restarting or under maintenance. The portal reconnects by itself as soon as it is back.'}
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, color: '#64748b', fontSize: '0.8rem', minHeight: 22, mb: 2 }}>
              {s.checking ? (
                <>
                  <CircularProgress size={14} thickness={5} sx={{ color: accent }} />
                  Checking the connection…
                </>
              ) : (
                <>Checking again in {left} s</>
              )}
            </Box>

            <Button variant="contained" disableElevation onClick={() => void checkService()} disabled={s.checking} sx={{ borderRadius: 2, px: 4, textTransform: 'none', fontWeight: 600 }}>
              Try now
            </Button>

            <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid #e2e8f0' }}>
              <Typography sx={{ fontSize: '0.75rem', color: '#64748b' }}>
                Your work on this page is kept and carries on when the connection is back.
              </Typography>
              <Typography sx={{ fontSize: '0.7rem', color: '#94a3b8', mt: 0.5 }}>
                {offline ? 'Offline' : 'Server not answering'} since {dayjs(s.since).format('hh:mm A')}
                {!offline && ' · if this lasts long, contact IT'}
              </Typography>
            </Box>
          </Paper>
        </Box>
      </Fade>

      <Snackbar open={backAgain} autoHideDuration={3500} onClose={() => setBackAgain(false)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity="success" variant="filled" onClose={() => setBackAgain(false)} sx={{ borderRadius: 2 }}>
          Connected again
        </Alert>
      </Snackbar>
    </>
  );
};

export default ServiceStatusScreen;
